import { useCallback, useEffect, useState } from 'react';
import { useMountedState } from 'react-use';
import { connect, useDispatch } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { IChecklistTemplateSelectOption } from 'CorePackage/AccountModule/interfaces/IChecklistTemplateSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getAccountKnoOrganizationsWithDistrictSelectOptions } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { KNM_CHECK_SHEETS } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmSetLoadingFlags } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import {
  CHECKLISTS_TITLE_BLOCK_ID,
  DURATION_DAYS_BLOCK_ID,
  DURATION_HOURS_BLOCK_ID,
  INSPECTION_TARGET_LABEL_BLOCK_ID,
  getChecklistBlockId,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { KnmLoadingFlagsEnum } from 'KnmPackage/MainModule/enums/KnmLoadingFlagsEnum';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectSelectOption';
import { getKnmErpErvkChecklistsSelectOptions, getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { useChecklistTemplate } from 'PersonalAccountPackage/CheckListModule/hooks/useChecklistTemplate';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { KnmErpAddTemplatesModal } from 'KnmErpPackage/MainModule/components/KnmErpAddTemplatesModal';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IErvkCheckList } from 'APIPackage/DictionariesModule/interfaces/IErvkCheckList';
import { getChecklistAnswerTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IChecklistAnswerTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IChecklistAnswerTypeSelectOption';

import { IKnmErpFormChecklist } from '../interfaces/IKnmErpFormChecklist';
import { KnmErpChecklist } from './KnmErpChecklist';
import { KnmErpEvents } from './KnmErpEvents';
import styles from './KnmErpChecklists.module.scss';

const COLLAPSE_TITLE_BASE = 'Проверочный лист';

interface IKnmErpChecklistsProps {
  checklistsHidden?: boolean;
  error: string;
  formKindValue: IKnmForm['kind'];
  formTypeValue: IKnmForm['type'];
  knmObjectsOptions: IKnmErpObjectSelectOption[] | undefined;
  watchOnly: boolean;
  settings: IAdminSetting[];
  knmData: IKnm | undefined;
}

interface IKnmErpChecklistsStoreProps {
  formRef: IKnmFormRef | undefined;
  knoOrganizationsSelectOptions: IUserOrganisationSelectOption[];
  ervkChecklistsSelectOptions: IDictionarySelectOption<IErvkCheckList>[] | undefined;
  checkListsAnswerTypesSelectOptions: IChecklistAnswerTypeSelectOption[] | undefined;
}

const KnmErpChecklistsWithoutStore = ({
  checklistsHidden,
  error,
  formKindValue,
  formTypeValue,
  knmObjectsOptions,
  watchOnly,
  settings,
  knmData,
  formRef,
  knoOrganizationsSelectOptions,
  ervkChecklistsSelectOptions,
  checkListsAnswerTypesSelectOptions,
}: IKnmErpChecklistsProps & IKnmErpChecklistsStoreProps) => {
  const isMounted = useMountedState();

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setChecklistsLoadingFlag = useCallback(
    (value: boolean) => dispatch(knmSetLoadingFlags(KnmLoadingFlagsEnum.CHECKLISTS, value)),
    [dispatch],
  );

  const [isChecklistModalVisible, setIsChecklistModalVisible] = useState(false);

  const openChecklistModal = useCallback(() => setIsChecklistModalVisible(true), []);
  const closeChecklistModal = useCallback(() => setIsChecklistModalVisible(false), []);

  const { isLoading: isGetChecklistTemplateLoading, getChecklistTemplate } = useChecklistTemplate();

  useEffect(() => {
    setChecklistsLoadingFlag(isGetChecklistTemplateLoading);
  }, [isGetChecklistTemplateLoading, setChecklistsLoadingFlag]);

  const addKnmChecklist = useCallback(
    async (template: IChecklistTemplateSelectOption) => {
      if (!watchOnly) {
        const getChecklistTemplateResponse = await getChecklistTemplate(template.id);

        if (getChecklistTemplateResponse?.success && isMounted()) {
          const newChecklistTemplate = getChecklistTemplateResponse.data;
          const knoOrganisationsSelectOption = knoOrganizationsSelectOptions?.find(
            option => option.id === template.knoOrganizationId,
          );
          const newChecklist: IKnmErpFormChecklist = {
            name: newChecklistTemplate?.name || undefined,
            approval: newChecklistTemplate?.approvalDetails || undefined,
            questions: newChecklistTemplate?.questions?.map(newChecklistTemplateQuestion => ({
              question: newChecklistTemplateQuestion.question || undefined,
              legalBase: newChecklistTemplateQuestion.npaProps || undefined,
              listId: uuidv4(),
            })),
            template: newChecklistTemplate,
            ...(knoOrganisationsSelectOption && {
              knoOrganization: knoOrganisationsSelectOption,
            }),
            isNew: true,
            listId: uuidv4(),
            ervkCheckList: undefined,
          };

          if (push) {
            push('checklists', newChecklist);
          }
        }
      }
    },
    [getChecklistTemplate, isMounted, knoOrganizationsSelectOptions, push, watchOnly],
  );

  const handleChecklistModalApply = useCallback(
    (template: IChecklistTemplateSelectOption) => addKnmChecklist(template),
    [addKnmChecklist],
  );

  const makeRemoveKnmChecklistHandler = (index: number) => () => !watchOnly && remove && remove('checklists', index);

  const isUseERVKParamTables = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );

  return (
    <Section title="Проверочные листы" id={KNM_CHECK_SHEETS} titleId={CHECKLISTS_TITLE_BLOCK_ID}>
      {checklistsHidden ? (
        <Row title="Добавление проверочных листов доступно только для плановых проверок" />
      ) : (
        <FieldBlock
          label="Реквизиты проверочного листа"
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={watchOnly || Boolean(error)}
              onClick={openChecklistModal}
            />
          }
        >
          {error && !checklistsHidden && <p className={knmSharedStyles.SectionErrorNotification}>{error}</p>}
        </FieldBlock>
      )}

      {!error && (
        <FieldArray name="checklists">
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const fieldValue: IKnmErpFormChecklist = fields.value?.[index];
              const id = getChecklistBlockId(fieldValue.listId);

              return (
                <Row key={id}>
                  <KnmCollapse
                    id={id}
                    theme="fields"
                    title={fieldValue?.isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
                    isNew={fieldValue?.isNew}
                    openByDefault={fieldValue?.isNew}
                    onClose={makeRemoveKnmChecklistHandler(index)}
                  >
                    <KnmErpChecklist
                      fieldName={name}
                      knmObjectsOptions={knmObjectsOptions}
                      ervkChecklistsSelectOptions={ervkChecklistsSelectOptions}
                      data={fieldValue}
                      isUseERVKParamTables={isUseERVKParamTables}
                      watchOnly={watchOnly}
                      checkListsAnswerTypesSelectOptions={checkListsAnswerTypesSelectOptions}
                    />
                  </KnmCollapse>
                </Row>
              );
            });
          }}
        </FieldArray>
      )}
      <Row>
        <FieldBlock label="Цели, задачи, предмет КНМ" id={INSPECTION_TARGET_LABEL_BLOCK_ID}>
          <TextareaField
            className={styles.KnmObjectivesField}
            inputClassName={styles.KnmObjectivesFieldInput}
            name="inspectionTarget"
            disabled={watchOnly}
          />
        </FieldBlock>
      </Row>

      {!isUseERVKParamTables && (
        <Row>
          <FieldBlock label="Характер КНМ" size="half">
            <SelectInput id="type-checklists" value={formTypeValue} size="medium" disabled />
          </FieldBlock>
          <FieldBlock label="Вид КНМ" size="half">
            <SelectInput id="kind-checklists" value={formKindValue} size="medium" disabled />
          </FieldBlock>
        </Row>
      )}

      <Row>
        <FieldBlock label="Срок проведения (дней)" id={DURATION_DAYS_BLOCK_ID} size="half" placementLabel="left">
          <TextInputField className="shared-row-field" id="durationDays" name="durationDays" disabled={watchOnly} />
        </FieldBlock>
        <FieldBlock label="Срок проведения (часов)" id={DURATION_HOURS_BLOCK_ID} size="half" placementLabel="left">
          <TextInputField className="shared-row-field" id="durationHours" name="durationHours" disabled={watchOnly} />
        </FieldBlock>
      </Row>

      <KnmErpEvents watchOnly={watchOnly} />

      <KnmErpAddTemplatesModal
        isChecklists
        visible={isChecklistModalVisible}
        onApply={handleChecklistModalApply}
        onClose={closeChecklistModal}
      />
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpChecklistsStoreProps => ({
  formRef: getKnmFormRef(state),
  knoOrganizationsSelectOptions: getAccountKnoOrganizationsWithDistrictSelectOptions(state),
  ervkChecklistsSelectOptions: getKnmErpErvkChecklistsSelectOptions(state),
  checkListsAnswerTypesSelectOptions: getChecklistAnswerTypesSelectOptions(state),
});

export const KnmErpChecklists = connect(mapStateToProps)(KnmErpChecklistsWithoutStore);
